// Contribution domain helpers — the pure domain layer for the contributions card,
// written to be shared by the Dashboard card today and the Manage Account card next.
//
// These are pure functions over the backend's `contributionSetup` + `irsLimits`
// contract (FM-1919 / 1920 / 1921 / 1922 / 1923 / 1925 / 1926). No React and no
// i18n: they return raw numbers plus interpolation params, and the calling
// component does the `t()`. That keeps them trivially unit-testable and lets both
// modules import them without reaching into each other (FRONTEND_RULES §6 — a
// module may never import from another module).
//
// Nothing here recomputes what the backend already decided. In particular the
// SECURE 2.0 catch-up tiering lives server-side (`irsLimits.applicable`), so the
// old hardcoded caps in the components are retired rather than reimplemented.

// Source colours are NOT modelled here — the dot is coloured by the
// `.contrib-rate--<source>` CSS modifier in dashboard.css, which keeps the token
// reference in one place (FRONTEND_RULES §7). Returning a colour from the domain
// layer as well would be a second, competing source of truth.

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const clampPct = (n) => Math.max(0, Math.min(100, num(n)));

/**
 * A source counts as "on" if any of its three figures is above zero — OR if it is
 * flagged `activeNoRate`, meaning money is going in on that source with no elected
 * rate to state (an employer interest credit, or catch-up money for someone over 50).
 * Those rows carry no figures by design, so a figures-only test dropped them.
 */
const rateIsActive = (r) => r?.activeNoRate === true
  || num(r?.value) > 0 || num(r?.pct) > 0 || num(r?.perCheckDollar) > 0;

/**
 * Sources the EMPLOYER funds on a formula rather than the participant electing.
 * They belong in the rate list (they are where the money comes from) but they are
 * not "contributions" the participant is making, so the row's pill says the money is
 * accruing rather than that the participant is actively contributing.
 */
const CREDIT_SOURCES = new Set(['paycredit', 'interest']);

/**
 * Display rows for the per-source rate list (FM-1921).
 *
 * `mode` says which unit the participant actually elected in; the backend always
 * populates BOTH `pct` and `perCheckDollar`, so the other unit becomes a secondary
 * caption without any recomputation here.
 *
 * One row is synthesized rather than read from `rates[]`: **derived** (FM-1922),
 * where contributions are posting with no elected rate on record. There is no
 * `rates[]` entry to show, so the row renders an "actively contributing" state
 * carrying the derived per-check figure.
 *
 * NOT synthesized: a catch-up row. The design shows "Catch-up (50+)" alongside the
 * elected sources, but the contract's `SourceRate.source` is strictly
 * pretax|roth|posttax — catch-up is *headroom on the limit*
 * (`applicable.catchUpAmount`), not an election. Rendering it here would mean
 * inventing an elected amount, so it is surfaced in the pacing section instead,
 * where `pacing()` already returns `catchUpAmount` and `missedCatchUp`.
 *
 * @param {object} setup  contributionSetup
 * @returns {Array<object>} rows — never null
 */
export const rateRows = (setup = {}) => {
  const rates = Array.isArray(setup.rates) ? setup.rates : [];
  const rows = rates.filter(rateIsActive).map((r) => ({
    source: r.source,
    // Backend supplies a portal-owned display label ("Employee Pre-Tax", "Roth",
    // "After-Tax"). Data-driven strings stay as data (CLAUDE.md §16); `labelKey`
    // is only the fallback when a payload omits it.
    //
    // ONE EXCEPTION — `posttax`. The payload calls it "After-Tax", but the money is
    // a VOLUNTARY after-tax contribution: the participant elected it, which is a
    // different thing from a mandatory/required after-tax contribution, and the
    // bare label reads as either. The portal owns that correction, so this source
    // always takes the i18n label and ignores whatever copy the payload sent —
    // otherwise the wording would vary per plan with the recordkeeper's phrasing
    // (the manage fixtures already carry both "After-Tax" and "Employee Voluntary
    // After-Tax" for the same source). Every other source still shows its payload
    // label verbatim.
    label: r.source === 'posttax' ? null : (r.label || null),
    labelKey: `dashboard.contributions.source.${r.source}`,
    mode: r.mode,
    value: num(r.value),
    pct: r.pct == null ? null : num(r.pct),
    perCheckDollar: r.perCheckDollar == null ? null : num(r.perCheckDollar),
    // Passed through, not hardcoded false: a source can be posting money with no
    // elected rate on record, and the row then renders a pill instead of a figure.
    activeNoRate: r.activeNoRate === true,
    // Which pill that row shows. Employer-funded sources accrue; they are not the
    // participant "actively contributing".
    pillKey: CREDIT_SOURCES.has(r.source)
      ? 'dashboard.contributions.accruing'
      : 'dashboard.contributions.activelyContributing',
  }));

  const state = setup.contributionState;
  if (!rows.length && state?.source === 'derived' && state.activelyContributing) {
    rows.push({
      source: 'derived',
      label: null,
      labelKey: 'dashboard.contributions.source.derived',
      mode: 'amount',
      value: num(state.derivedPerCheck),
      pct: null,
      perCheckDollar: num(state.derivedPerCheck),
      activeNoRate: true,
      pillKey: 'dashboard.contributions.activelyContributing',
    });
  }

  return rows;
};

/**
 * Which employer-match statement can honestly be made (FM-1923).
 *
 * Returns exactly one of `confirmed | estimated | unavailable`, or null when the
 * payload carries no availability record at all. This replaces the old
 * unconditional "Full match captured" chip, which asserted a confirmed match
 * regardless of whether PCS had the spec or the funding.
 *
 * @returns {{kind: string, params: object}|null}
 */
export const matchNote = (setup = {}, { employer = '', amount } = {}) => {
  const ma = setup.matchAvailability;
  if (!ma) return null;

  // `employerMatchRate` is documented as a ratio (0.5 = 50%), but tolerate a
  // payload that already sends percent so the copy never reads "0.5%".
  const raw = setup.employerMatchRate;
  const ratePct = raw == null ? null : Math.round(raw <= 1 ? raw * 100 : raw);

  // A match sentence names two figures ("matches {ratePct}% of your first {pct}%").
  // Without both, there is no honest sentence to write — and defaulting `ratePct` to
  // 100 asserted a full match the payload never stated. Fall back to the
  // availability wording, which says only what we know.
  if (ratePct == null || setup.employerMatchPct == null) {
    return { kind: 'unavailable', params: { employer, ratePct: null, pct: null, amount: amount ?? null } };
  }

  const params = {
    employer,
    ratePct,
    pct: setup.employerMatchPct,
    // The copy is annual ("all $X/yr"), so callers pass the YTD employer figure.
    // `employerMatchDollar` is PER PAYCHECK and would understate it by ~26×.
    amount: amount ?? null,
  };

  if (ma.confidence === 'confirmed') return { kind: 'confirmed', params };
  if (ma.confidence === 'estimated') return { kind: 'estimated', params };
  return { kind: 'unavailable', params };
};

/**
 * The employer's side of the story, for plans where it is NOT a match.
 *
 * A cash-balance plan credits the account on a formula — a percent of pay, plus a
 * guaranteed interest credit on the hypothetical balance — and none of it is
 * contingent on the participant deferring. `matchAvailability` can only express a
 * match, so before this the card went silent on those plans: correct (asserting a
 * match would be wrong) but incomplete, since the employer is in fact the main
 * source of the money.
 *
 * Returns `creditConfirmed | creditEstimated`, or null when the plan has no credit
 * record — in which case the caller falls back to `matchNote`.
 *
 * Deliberately NOT modelled: the design's "Combined contribution rate · 10% of pay"
 * headline, which reaches that figure by adding a 5%-of-pay credit to a 5% APR
 * interest rate. Those are different quantities over different bases; summing them
 * is the same category error this card already refuses when it declines to combine a
 * percentage rate with a dollar rate.
 *
 * @param {object} setup contributionSetup
 * @param {{employer?: string, ytdAmount?: number}} ctx
 * @returns {{kind: string, params: object}|null}
 */
export const employerCreditNote = (setup = {}, { employer = '', ytdAmount } = {}) => {
  const credit = setup.employerCredit;
  if (!credit) return null;

  const params = {
    employer,
    // Percent of pay credited by the plan formula.
    formulaPct: credit.formulaPct ?? null,
    // Guaranteed crediting rate on the hypothetical balance (APR).
    ratePct: credit.ratePct ?? null,
    // Year-to-date credited. The credit record's own figure WINS over the caller's
    // employer-YTD: the copy names both the pay credit and the interest credit, so it
    // needs their total, whereas `erYtd` on a credit plan carries the pay credits
    // alone and would understate what the sentence claims.
    amount: credit.ytdAmount ?? ytdAmount ?? null,
  };

  return {
    kind: credit.confidence === 'estimated' ? 'creditEstimated' : 'creditConfirmed',
    params,
  };
};

/**
 * The single employer statement this plan supports — a credit where the employer
 * funds on a formula, otherwise a match. One call site, so the card never has to
 * know which kind of plan it is looking at.
 */
export const employerNote = (setup = {}, ctx = {}) =>
  employerCreditNote(setup, ctx) || matchNote(setup, ctx);

/**
 * How to word the "who put this in" split under the period total.
 *
 * On a match plan the employer's half IS a match. On a cash-balance plan it is a
 * credit, and calling it a match misdescribes the money — the participant could stop
 * contributing entirely and the credit would still post.
 *
 * @returns {'match'|'credit'}
 */
export const employerSplitKind = (setup = {}) => (setup.employerCredit ? 'credit' : 'match');

/**
 * How the auto-increase block should render (FM-1920 + FM-1919).
 *
 *  - `hidden`      — the plan doesn't offer auto-increase; render no section at all
 *  - `planManaged` — the plan doesn't allow self-service rate changes
 *  - `readonly`    — available, but not editable from Web
 *  - `editable`    — full toggle + Manage link
 *
 * Absent flags default to visible/editable so an older payload (or the mock) keeps
 * working rather than silently hiding the feature.
 */
export const autoIncreaseMode = (setup = {}) => {
  if (setup.autoIncreaseAvailable === false) return 'hidden';
  if (setup.canChangeRate === false) return 'planManaged';
  if (setup.autoIncreaseWebEditable === false) return 'readonly';
  return 'editable';
};

/**
 * Pacing against the participant's applicable IRS limit (FM-1925 + FM-1926).
 *
 * Every percentage is clamped to 0–100 — the previous implementation divided
 * without clamping, so a participant over the cap produced a >100% width that
 * overflowed its track and silently hid the second segment.
 *
 * Returns null when no limit is known, so the caller renders nothing rather than
 * asserting a cap it can't stand behind.
 */
export const pacing = (setup = {}, irsLimits = {}) => {
  const proj = setup.projection || null;
  const applicable = irsLimits.applicable || null;

  // Prefer the catch-up-inclusive applicable limit; the flat plan limit ignores
  // catch-up entirely and understates the cap for anyone 50+.
  const limit =
    applicable?.employeeElectiveApplicable ?? proj?.applicableLimit ?? irsLimits.employeeElectiveLimit ?? 0;
  if (!num(limit)) return null;

  const ytd = num(proj?.ytdContributed ?? setup.irsContributedYtd);
  const projected = proj?.projectedTotal == null ? null : num(proj.projectedTotal);

  const ytdPct = clampPct((ytd / limit) * 100);
  const projectedPct = projected == null ? ytdPct : clampPct((projected / limit) * 100);
  const willHitMax = proj ? proj.willNotMax === false : projected != null && projected >= limit;

  const catchUpAmount = num(applicable?.catchUpAmount);
  // The base (pre-catch-up) elective limit, for the tiered bar. Only meaningful when
  // a catch-up tier actually widens the applicable limit; clamped so a payload whose
  // flat limit exceeds the applicable one can't draw a base band past the track.
  const base = Math.min(num(irsLimits.employeeElectiveLimit) || num(limit), num(limit));

  return {
    limit: num(limit),
    ytd,
    projected,
    ytdPct,
    // Width of the "projected on top of saved" segment, so the two never overlap.
    additionalPct: Math.max(0, projectedPct - ytdPct),
    willHitMax,
    shortfall: willHitMax || projected == null ? 0 : Math.max(0, num(limit) - projected),
    catchUpAmount,
    // SECURE 2.0 tier for the catch-up band: 'standard' (50–59, 64+) or 'enhanced'
    // (the 60–63 super catch-up). Derived from the amount when an older payload
    // carries no tier, so the band never renders unlabelled.
    catchUpTier: applicable?.catchUpTier ?? (catchUpAmount > 0 ? 'standard' : 'none'),
    base,
    missedCatchUp: proj?.missedCatchUpOpportunity === true,
  };
};

/**
 * Employment statuses that mean no payroll feed reaches this plan.
 *
 * `inactive` belongs here alongside `terminated`: a former employer's plan keeps its
 * balance invested but nothing new comes in, so a contributions widget has nothing to
 * describe. Matching only `terminated` left this helper disagreeing with
 * `portfolio.benchmark.js`, which treats both as historical — the same participant
 * could get a contributions card on one screen and a historical benchmark on the
 * other.
 */
export const NO_PAYROLL_STATUSES = new Set(['terminated', 'inactive']);

/**
 * Does this employment status mean "no payroll feed"? Exported so the module hook's
 * WHILE-PENDING gate can ask the same question the settled gate asks — it checked only
 * 'terminated', so an 'inactive' account rendered the card with skeletons and then had
 * it vanish when /manage-account resolved, which is the flicker the pending gate exists
 * to prevent.
 */
export const hasNoPayroll = (employmentStatus) =>
  NO_PAYROLL_STATUSES.has(String(employmentStatus || '').toLowerCase());

/**
 * Whether the contributions card renders at all.
 *
 * A terminated or inactive participant has no payroll feed, so the widget is
 * suppressed wholesale (FM-1918) and the neighbouring card takes the row. Otherwise the card
 * needs *something* true to say: an active source, posting contributions, period
 * activity, or a YTD figure. When none of that holds it renders nothing — no empty
 * shell, no zero-state placeholder.
 */
export const contributionsVisible = ({ setup = {}, employmentStatus, periods } = {}) => {
  if (hasNoPayroll(employmentStatus)) return false;
  if (setup.contributionState?.activelyContributing) return true;
  if ((Array.isArray(setup.rates) ? setup.rates : []).some(rateIsActive)) return true;
  const hasActivity = Object.values(periods || {}).some((p) => num(p?.you) + num(p?.match) > 0);
  if (hasActivity) return true;
  return num(setup.irsContributedYtd) > 0;
};
